import { ListingTile, TileRate, TileRateApartment } from '@nocowanie/noc-api';

import { wordPluralisation } from '@nocowanie/common-ui/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { APARTMENT_TYPE_LABEL } from '@app/consts';
import { useAccommodationRatePlanFeatures } from '@app/hooks/useAccommodationRatePlanFeatures';

import s from './accommodation-tile-lite.module.css';
import { TileIcon } from './tile-icon';

type T = AccommodationTileProps['t'];

const pluralise = (count: number, forms: Record<string, string>) =>
    wordPluralisation(count, forms.singular, forms.plural, forms.genitivePlural);

/** Same markup as `AccommodationTileRoomInfo`, without Chakra runtime styling */
const RoomInfo = ({ apartment, t }: { apartment: TileRateApartment; t: T }) => {
    const { type, singleBeds, doubleBeds, size, defaultGuests } = apartment;
    const singleBedsCount = +(singleBeds ?? 0);
    const doubleBedsCount = +(doubleBeds ?? 0);
    const bedsCount = singleBedsCount + doubleBedsCount;

    const bedForms = t('roomInfo.bed', { returnObjects: true }) as Record<string, string>;
    const singleBedsLabel = pluralise(
        singleBedsCount,
        t('roomInfo.single', { returnObjects: true }) as Record<string, string>,
    );
    const doubleBedsLabel = pluralise(
        doubleBedsCount,
        t('roomInfo.double', { returnObjects: true }) as Record<string, string>,
    );

    const bedBreakdown =
        singleBedsCount > 0 && doubleBedsCount > 0
            ? `(${singleBedsCount} ${singleBedsLabel}, ${doubleBedsCount} ${doubleBedsLabel})`
            : singleBedsCount > 0
              ? singleBedsLabel
              : doubleBedsCount > 0
                ? doubleBedsLabel
                : '';

    const guestCapacityLabel =
        defaultGuests && type
            ? ` ${defaultGuests}-${APARTMENT_TYPE_LABEL[type].guestCapacityLabel}`
            : null;

    return (
        <div className={s.roomInfo}>
            {type ? (
                <>
                    <span className={s.roomTypeDesktop}>
                        {APARTMENT_TYPE_LABEL[type].label}
                        {guestCapacityLabel}
                    </span>
                    <span className={s.roomTypeMobile}>
                        {APARTMENT_TYPE_LABEL[type].label}
                        {size || bedsCount ? ':' : null}
                    </span>
                </>
            ) : null}
            <div className={s.roomFacts}>
                {size ? (
                    <span className={bedsCount ? s.roomSizeWithBeds : undefined}>
                        {size} m<sup>2</sup>
                    </span>
                ) : null}
                {bedsCount ? (
                    <span>
                        {bedsCount} {pluralise(bedsCount, bedForms)}
                        <span className={s.hideBelowMd}> {bedBreakdown}</span>
                    </span>
                ) : null}
            </div>
        </div>
    );
};

/** Same markup as `AccommodationTileRatePlan`, without Chakra runtime styling */
const RatePlan = ({ rate, t }: { rate: TileRate; t: T }) => {
    const features = useAccommodationRatePlanFeatures(t, rate);

    if (!features?.length) {
        return null;
    }

    const featureClassName = ({
        color,
        fontWeight,
        showOnMobile,
    }: {
        color?: string;
        fontWeight?: string;
        showOnMobile?: boolean;
    }) => {
        if (fontWeight === 'bold') {
            return showOnMobile ? s.featureStrong : s.featureStrongDesktopOnly;
        }

        if (color) {
            return s.featureGreen;
        }

        return showOnMobile ? undefined : s.featureDesktopOnly;
    };

    return (
        <ul role={'list'} data-testid={'acc-tile-features'} className={s.features}>
            {features.map(feature =>
                feature.content ? (
                    <li
                        key={feature.key}
                        data-testid={feature.dataTestId}
                        className={featureClassName(feature)}
                    >
                        <TileIcon icon={IconsLinear.Tick} className={s.featureIcon} />
                        <span dangerouslySetInnerHTML={{ __html: feature.content }} />
                    </li>
                ) : null,
            )}
        </ul>
    );
};

export const TileDetails = ({ tile, t }: { tile: ListingTile; t: T }) => {
    const isInquiry = tile.source === ListingTile.source.NOC;
    const isOTA = tile.source === ListingTile.source.NOP;
    const { features, rate } = tile;

    return (
        <div data-testid={'acc-tile-details'} className={isInquiry ? s.detailsInquiry : s.details}>
            {isInquiry && features?.promoText ? (
                <p data-testid={'acc-tile-promo-text'} className={s.promoText}>
                    {features.promoText}
                </p>
            ) : null}

            {isOTA && rate ? (
                <>
                    {rate.apartment ? <RoomInfo apartment={rate.apartment} t={t} /> : null}
                    <RatePlan rate={rate} t={t} />
                </>
            ) : null}
        </div>
    );
};
